import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import {
  CheckCircle,
  Globe,
  Users,
  TrendingUp,
  Award,
  Handshake,
  MapPin,
  Phone,
  Mail,
  DollarSign,
  BookOpen,
} from "lucide-react"

const PartnersPage: React.FC = () => {
  const partnershipBenefits = [
    {
      icon: TrendingUp,
      title: "Growing Market Demand",
      description: "Tap into the expanding multipurpose vehicle market with proven demand across multiple industries",
    },
    {
      icon: Award,
      title: "Premium Brand Recognition",
      description: "Represent a trusted brand with over 20 years of innovation and excellence in the industry",
    },
    {
      icon: Users,
      title: "Comprehensive Support",
      description: "Receive full sales, technical, and marketing support to ensure your success",
    },
    {
      icon: Globe,
      title: "Exclusive Territories",
      description: "Secure exclusive distribution rights in your designated territory with protected boundaries",
    },
    {
      icon: DollarSign,
      title: "Attractive Margins",
      description: "Competitive pricing structure with attractive profit margins and volume incentives",
    },
    {
      icon: BookOpen,
      title: "Training & Certification",
      description: "Comprehensive training programs for sales, technical, and service teams",
    },
  ]

  const supportPrograms = [
    {
      title: "Sales Support",
      description: "Dedicated sales support team to help close deals and provide technical expertise",
      features: [
        "Lead generation assistance",
        "Sales training programs",
        "Technical presentations",
        "Proposal support",
      ],
    },
    {
      title: "Marketing Support",
      description: "Comprehensive marketing materials and co-op advertising programs",
      features: ["Marketing collateral", "Digital assets", "Trade show support", "Co-op advertising funds"],
    },
    {
      title: "Technical Training",
      description: "Extensive technical training for your service and support teams",
      features: ["Product training", "Service certification", "Parts management", "Warranty support"],
    },
    {
      title: "Business Development",
      description: "Strategic business development support to grow your market presence",
      features: ["Market analysis", "Business planning", "Territory development", "Performance metrics"],
    },
  ]

  const territories = [
    {
      region: "New South Wales",
      status: "Available",
      population: "8.2M",
      keyMarkets: ["Sydney Metro", "Newcastle", "Wollongong", "Regional NSW"],
    },
    {
      region: "Victoria",
      status: "Established",
      population: "6.7M",
      keyMarkets: ["Melbourne Metro", "Geelong", "Ballarat", "Regional Victoria"],
    },
    {
      region: "Queensland",
      status: "Available",
      population: "5.2M",
      keyMarkets: ["Brisbane Metro", "Gold Coast", "Sunshine Coast", "Regional QLD"],
    },
    {
      region: "Western Australia",
      status: "Available",
      population: "2.7M",
      keyMarkets: ["Perth Metro", "Regional WA", "Mining Districts"],
    },
    {
      region: "South Australia",
      status: "Available",
      population: "1.8M",
      keyMarkets: ["Adelaide Metro", "Regional SA"],
    },
    {
      region: "Tasmania",
      status: "Available",
      population: "540K",
      keyMarkets: ["Hobart", "Launceston", "Regional TAS"],
    },
  ]

  const requirements = [
    {
      category: "Business Requirements",
      items: [
        "Established business with proven track record",
        "Strong financial position and credit rating",
        "Existing customer base in target markets",
        "Commitment to exclusive territory representation",
      ],
    },
    {
      category: "Technical Capabilities",
      items: [
        "Service facility with qualified technicians",
        "Parts inventory and logistics capability",
        "Technical support infrastructure",
        "Quality management systems",
      ],
    },
    {
      category: "Market Presence",
      items: [
        "Strong relationships with target customers",
        "Understanding of local market conditions",
        "Established sales and marketing capabilities",
        "Commitment to brand representation",
      ],
    },
  ]

  const applicationProcess = [
    {
      step: "1",
      title: "Initial Inquiry",
      description: "Submit your partnership inquiry with basic company information",
    },
    {
      step: "2",
      title: "Qualification Review",
      description: "We review your application and assess fit with our partnership criteria",
    },
    {
      step: "3",
      title: "Business Discussion",
      description: "Detailed discussions about territory, market potential, and business model",
    },
    {
      step: "4",
      title: "Site Visit & Evaluation",
      description: "On-site evaluation of facilities, capabilities, and market presence",
    },
    {
      step: "5",
      title: "Agreement Negotiation",
      description: "Finalize partnership terms, territory boundaries, and support programs",
    },
    {
      step: "6",
      title: "Training & Launch",
      description: "Comprehensive training program and official partnership launch",
    },
  ]

  const testimonials = [
    {
      name: "David Chen",
      company: "Melbourne Equipment Solutions",
      territory: "Victoria",
      quote:
        "Partnering with MPV has been transformational for our business. The product quality, support, and market demand have exceeded our expectations.",
      results: "300% growth in first 2 years",
    },
    {
      name: "Sarah Williams",
      company: "Industrial Solutions WA",
      territory: "Western Australia",
      quote:
        "The comprehensive training and ongoing support from MPV has enabled us to become the leading Multihog distributor in our region.",
      results: "Market leader in 18 months",
    },
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Partnership Opportunities
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Partner with
              <br />
              <span className="text-indigo-400">Australia's Leading</span>
              <br />
              Multihog Specialists
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Join our exclusive distributor network and represent premium multipurpose vehicles in your territory.
              Benefit from over 20 years of innovation, comprehensive support, and growing market demand.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="#apply"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Apply for Partnership
              </Link>
              <Link
                to="#territories"
                className="border border-white text-white hover:bg-white hover:text-indigo-900 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Available Territories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Stats */}
      <section className="py-16 bg-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-indigo-600 mb-2">20+</div>
                <div className="text-gray-600">Years in Business</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-indigo-600 mb-2">40+</div>
                <div className="text-gray-600">Specialized Attachments</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-indigo-600 mb-2">500+</div>
                <div className="text-gray-600">Units Deployed</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-indigo-600 mb-2">95%</div>
                <div className="text-gray-600">Partner Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Why Partner with MPV?</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join a proven partnership program that provides everything you need to succeed in the multipurpose
                vehicle market.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {partnershipBenefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="bg-indigo-100 rounded-full p-3 w-12 h-12 mb-4 flex items-center justify-center">
                    <benefit.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Support Programs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Comprehensive Partner Support</h2>
              <p className="text-lg text-gray-600">
                We provide extensive support programs to ensure your success as an MPV partner
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {supportPrograms.map((program, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{program.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{program.description}</p>
                  <ul className="space-y-3">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-indigo-600 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Available Territories */}
      <section id="territories" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Available Territories</h2>
              <p className="text-lg text-gray-600">Explore partnership opportunities in key Australian markets</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {territories.map((territory, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{territory.region}</h3>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        territory.status === "Available" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {territory.status}
                    </span>
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <Users className="h-4 w-4 mr-2" />
                      <span>Population: {territory.population}</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Key Markets:</h4>
                    <div className="flex flex-wrap gap-2">
                      {territory.keyMarkets.map((market, idx) => (
                        <span key={idx} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                          {market}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Partnership Requirements</h2>
              <p className="text-lg text-gray-600">
                We seek partners who meet our standards for excellence and market representation
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {requirements.map((req, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{req.category}</h3>
                  <ul className="space-y-4">
                    {req.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section id="apply" className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Partnership Application Process</h2>
              <p className="text-lg text-gray-600">Our structured approach ensures the right fit for both parties</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {applicationProcess.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                    <div className="bg-indigo-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  {index < applicationProcess.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <div className="w-8 h-0.5 bg-indigo-300"></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Testimonials */}
      <section className="py-20 bg-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Partner Success Stories</h2>
              <p className="text-lg text-gray-600">Hear from our successful partners about their experience with MPV</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center mb-6">
                    <Handshake className="h-8 w-8 text-indigo-600 mr-3" />
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{testimonial.name}</h3>
                      <p className="text-gray-600">{testimonial.company}</p>
                      <p className="text-indigo-600 text-sm">{testimonial.territory}</p>
                    </div>
                  </div>
                  <blockquote className="text-gray-700 italic mb-4">"{testimonial.quote}"</blockquote>
                  <div className="bg-indigo-100 rounded-lg p-3">
                    <p className="text-indigo-800 font-semibold text-sm">Result: {testimonial.results}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Partner with MPV?</h2>
              <p className="text-lg text-gray-600">
                Contact our partnership team to discuss opportunities in your territory
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Partnership Inquiry</h3>
                <form className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="First Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="Email Address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <input
                    type="text"
                    placeholder="Company Name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Select Territory of Interest</option>
                    <option value="nsw">New South Wales</option>
                    <option value="qld">Queensland</option>
                    <option value="wa">Western Australia</option>
                    <option value="sa">South Australia</option>
                    <option value="tas">Tasmania</option>
                    <option value="other">Other/Multiple</option>
                  </select>
                  <textarea
                    placeholder="Tell us about your business and partnership interest"
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  ></textarea>
                  <button
                    type="submit"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Submit Partnership Inquiry
                  </button>
                </form>
              </div>

              <div className="bg-indigo-600 text-white rounded-xl p-8">
                <h3 className="text-xl font-bold mb-6">Partnership Team</h3>
                <div className="space-y-6">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 mr-3" />
                    <div>
                      <p className="font-semibold">Phone</p>
                      <p className="text-indigo-200">+61 3 1234 5678</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 mr-3" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <p className="text-indigo-200">partnerships@mpv-multihog.com.au</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 mr-3 mt-1" />
                    <div>
                      <p className="font-semibold">Address</p>
                      <p className="text-indigo-200">
                        123 Industrial Drive
                        <br />
                        Melbourne, VIC 3000
                        <br />
                        Australia
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-indigo-500">
                  <h4 className="font-semibold mb-3">Partnership Benefits</h4>
                  <ul className="space-y-2 text-indigo-200">
                    <li>• Exclusive territory rights</li>
                    <li>• Comprehensive training</li>
                    <li>• Marketing support</li>
                    <li>• Technical assistance</li>
                    <li>• Competitive margins</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Join Australia's Premier Multihog Network</h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Take advantage of this opportunity to represent premium multipurpose vehicles in your territory. Join our
              successful partner network today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="#apply"
                className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Start Partnership Application
              </Link>
              <Link
                to="/contact"
                className="border border-white text-white hover:bg-white hover:text-indigo-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Schedule Discussion
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default PartnersPage
